import { useState } from 'react';

import { Box, Chip, Link, Alert, Stack, Divider, Collapse, IconButton, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { Nudge } from 'src/services/ai/ai.types';
import { useGetAiNudgesQuery } from 'src/services/ai/ai.service';

import Iconify from 'src/components/iconify';

export default function AiNudgeBanner() {
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const router = useRouter();

  const { data } = useGetAiNudgesQuery();

  if (dismissed || !data?.nudges?.length) return null;

  const dedupe = (nudges: Nudge[]) => nudges.filter((n, i, arr) => arr.findIndex((x) => x.title === n.title) === i);
  const staleJobs = dedupe(data.nudges.filter((n) => n.type === 'stale_job'));
  const staleApplicants = dedupe(data.nudges.filter((n) => n.type === 'stale_applicant'));

  const summary = [
    staleJobs.length ? `${staleJobs.length} stale job${staleJobs.length > 1 ? 's' : ''}` : '',
    staleApplicants.length ? `${staleApplicants.length} applicant${staleApplicants.length > 1 ? 's' : ''} stalled in pipeline` : '',
  ]
    .filter(Boolean)
    .join(' · ');

  const handleNudgeClick = (nudge: Nudge) => {
    setExpanded(false);
    if (nudge.type === 'stale_job' || nudge.type === 'stale_applicant') {
      router.push(paths.dashboard.jobs.detail(nudge.id));
    }
  };

  return (
    <Box sx={{ width: '100%', px: 0.5, pb: 1 }}>
      <Alert
        severity="info"
        icon={<Iconify icon="mdi:robot-outline" width={20} />}
        action={
          <Stack direction="row" alignItems="center" gap={0.5}>
            <IconButton size="small" onClick={() => setExpanded((v) => !v)}>
              <Iconify icon={expanded ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'} width={18} />
            </IconButton>
            <IconButton size="small" onClick={() => setDismissed(true)}>
              <Iconify icon="eva:close-fill" width={18} />
            </IconButton>
          </Stack>
        }
      >
        <Stack direction="row" alignItems="center" gap={1} sx={{ cursor: 'pointer' }} onClick={() => setExpanded((v) => !v)}>
          <Typography variant="body2" fontWeight={600}>
            AI Insight
          </Typography>
          <Typography variant="body2">{summary}</Typography>
        </Stack>

        <Collapse in={expanded}>
          <Divider sx={{ my: 1 }} />
          <Stack gap={0.75}>
            {staleJobs.length > 0 && (
              <>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  STALE JOBS
                </Typography>
                {staleJobs.map((n) => (
                  <Stack key={n.id} direction="row" alignItems="center" gap={1}>
                    <Chip label={`${n.daysIdle}d`} size="small" color="info" variant="outlined" sx={{ minWidth: 40 }} />
                    <Link
                      variant="body2"
                      underline="hover"
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleNudgeClick(n)}
                    >
                      {n.title}
                    </Link>
                    <Typography variant="caption" color="text.secondary">
                      {n.detail}
                    </Typography>
                  </Stack>
                ))}
              </>
            )}

            {staleJobs.length > 0 && staleApplicants.length > 0 && <Divider sx={{ my: 0.5 }} />}

            {staleApplicants.length > 0 && (
              <>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  STALE APPLICANTS
                </Typography>
                {staleApplicants.map((n) => (
                  <Stack key={n.id} direction="row" alignItems="center" gap={1}>
                    <Chip label={`${n.daysIdle}d`} size="small" color="default" variant="outlined" sx={{ minWidth: 40 }} />
                    <Link
                      variant="body2"
                      underline="hover"
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleNudgeClick(n)}
                    >
                      {n.title}
                    </Link>
                    <Typography variant="caption" color="text.secondary">
                      {n.detail}
                    </Typography>
                  </Stack>
                ))}
              </>
            )}
          </Stack>
        </Collapse>
      </Alert>
    </Box>
  );
}
